import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';

// Types
interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  department: string;
  phone?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

interface AuthContextType extends AuthState {
  /**
   * `onBeforeSuccess` runs AFTER credentials are validated and the token is
   * stored, but BEFORE the authenticated state flips (which is what triggers
   * the redirect). It lets the login screen play a short "systems online"
   * transition before handing off to the app. A wrong password still fails
   * fast — the callback never runs on error.
   */
  login: (email: string, password: string, onBeforeSuccess?: () => Promise<void>) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  isSuperAdmin: () => boolean;
}

// Initial state - isLoading starts as true to prevent redirect before auth check completes
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          const user = await authService.getCurrentUser();
          dispatch({ type: 'AUTH_SUCCESS', payload: user });
        } else {
          // No token found, stop loading
          dispatch({ type: 'LOGOUT' });
        }
      } catch (error) {
        localStorage.removeItem('authToken');
        dispatch({ type: 'AUTH_ERROR', payload: 'Session expired' });
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email: string, password: string, onBeforeSuccess?: () => Promise<void>) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authService.login({ email, password });
      localStorage.setItem('authToken', response.token);
      if ((response as any).sessionId) {
        localStorage.setItem('sessionId', String((response as any).sessionId));
      }
      // Credentials are valid and the token is stored. Give the caller a chance
      // to play a transition before the app takes over.
      if (onBeforeSuccess) {
        try {
          await onBeforeSuccess();
        } catch {
          /* a transition hiccup must never block a successful sign-in */
        }
      }
      dispatch({ type: 'AUTH_SUCCESS', payload: response.user });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  const logout = () => {
    const sessionId = localStorage.getItem('sessionId');
    localStorage.removeItem('authToken');
    localStorage.removeItem('sessionId');
    authService.logout(sessionId ? parseInt(sessionId) : undefined);
    dispatch({ type: 'LOGOUT' });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const isSuperAdmin = () => {
    return state.user?.role === 'SUPER_ADMIN';
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    clearError,
    isSuperAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export type { User, AuthState };